import type { ReactNode } from 'react';
import { Card } from '../ui/card';
import { PanelTitle, PageDescription } from '../layout/Typography';
import { cn } from '../../lib/utils';
import { DescriptionHint } from './DescriptionHint';

interface SettingsRowProps {
  /** Heading text shown beside the control. */
  title: ReactNode;
  /** Optional supporting copy rendered below the title. */
  description?: ReactNode;
  /**
   * Optional inline badges/chips placed next to the title. Wrapped in a
   * `flex-wrap` row so multiple status pills line-break cleanly on narrow
   * widths.
   */
  badges?: ReactNode;
  /**
   * The control that anchors the row — typically a `Switch`, `Select`,
   * `Button`, or a tight `Label + Switch` group. Rendered right-aligned and
   * sized to fit in a `shrink-0` flex slot.
   */
  control: ReactNode;
  /**
   * Heading level. Defaults to `'h2'`; switch to `'h3'` when the row sits
   * inside a section that already owns the `h2`.
   */
  as?: 'h2' | 'h3';
  /**
   * Visual container.
   * - `'card'` (default) wraps the row in a bordered `Card` — use when the
   *   row stands alone in a `PageLayout` body, side-by-side with
   *   `SectionCard`s.
   * - `'plain'` skips the `Card` wrapper and renders just the row layout,
   *   for use inside a `SectionCard` body where the parent already owns
   *   the border + padding.
   */
  variant?: 'card' | 'plain';
  className?: string;
  /** Lands on the root element; lets callers preserve stable selectors. */
  'data-testid'?: string;
}

/**
 * One-line settings/control row rendered inside a bordered card.
 *
 * The "title (+ badges) + description on the left, control on the right"
 * shape recurs across the admin surface — appearance defaults, feature flag
 * toggles, per-agent availability switches, model-key rows. Each occurrence
 * was hand-rolling the same `<Card> + flex justify-between` skeleton with
 * slightly different paddings and heading sizes, so they drifted apart.
 *
 * Reach for `SettingsRow` whenever the row reads as "label + status + one
 * primary control". Reach for `SectionCard` instead when the card has body
 * content (a list, a form, a chart) underneath the heading.
 */
export function SettingsRow({
  title,
  description,
  badges,
  control,
  as = 'h2',
  variant = 'card',
  className,
  'data-testid': dataTestId,
}: SettingsRowProps) {
  const titleRow = (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {badges ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <PanelTitle as={as} size="sm">
            {title}
          </PanelTitle>
          {badges}
        </div>
      ) : (
        <PanelTitle as={as} size="sm">
          {title}
        </PanelTitle>
      )}
      {description ? (
        <DescriptionHint className="sm:hidden" ariaLabel="About this setting">
          {description}
        </DescriptionHint>
      ) : null}
    </div>
  );

  const body = (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        {titleRow}
        {description ? (
          <PageDescription className="mt-0.5 max-sm:hidden text-xs">{description}</PageDescription>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">{control}</div>
    </div>
  );

  if (variant === 'plain') {
    return (
      <div data-testid={dataTestId} className={className}>
        {body}
      </div>
    );
  }

  return (
    <Card
      data-testid={dataTestId}
      className={cn('border-border/30 p-4 transition-[border-color]', className)}
    >
      {body}
    </Card>
  );
}
